/**
 * FunnelModal
 *
 * Full-screen modal overlay for PDP funnel experiences.
 * Supports page_content blocks when configured, falls back to hardcoded layout.
 */

import styled from '@emotion/styled';
import { useState, useCallback, useRef, useEffect } from 'react';
import type { FunnelOffer, StepPageContent, StepPageBlock, DisplaySettings } from '../types';
import type { BlockCallbacks } from './ComponentRenderers';
import { useOfferImage } from '../use-offer-image';
import { normalizeBlocks, renderBlocks, blocksHaveCta } from './BlockRenderer';
import { withResolvedFunnelOfferPrice } from '../offer-pricing';

const CLOSE_ANIMATION_MS = 260;

interface AnimatedProps {
  $closing: boolean;
}

const Overlay = styled.div<AnimatedProps>`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
  animation: ${({ $closing }) => ($closing ? 'funnelFadeOut' : 'funnelFadeIn')} 0.25s ease forwards;

  @keyframes funnelFadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }

  @keyframes funnelFadeOut {
    from { opacity: 1; }
    to { opacity: 0; }
  }

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const ModalCard = styled.div<AnimatedProps>`
  background: ${({ theme }) => theme.colors.background.primary};
  border-radius: ${({ theme }) => theme.radius.regular};
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
  width: 90%;
  max-width: 480px;
  max-height: 90vh;
  overflow-y: auto;
  padding: 32px 28px 28px;
  position: relative;
  animation: ${({ $closing }) => ($closing ? 'funnelSlideDown' : 'funnelSlideUp')} 0.25s ease forwards;

  @keyframes funnelSlideUp {
    from { transform: translateY(20px); opacity: 0; }
    to { transform: translateY(0); opacity: 1; }
  }

  @keyframes funnelSlideDown {
    from { transform: translateY(0); opacity: 1; }
    to { transform: translateY(20px); opacity: 0; }
  }

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: 14px;
  right: 14px;
  background: none;
  border: none;
  cursor: pointer;
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: 20px;
  padding: 4px;
  line-height: 1;
  z-index: 1;
`;

const BlockContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const OfferImage = styled.img`
  width: 100%;
  max-height: 280px;
  object-fit: contain;
  border-radius: 8px;
  margin-bottom: 16px;
`;

const Headline = styled.h2`
  font-size: 22px;
  font-weight: 700;
  margin: 0 0 8px;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const Description = styled.p`
  font-size: 14px;
  color: ${({ theme }) => theme.colors.text.secondary};
  margin: 0 0 16px;
`;

const PriceRow = styled.div`
  display: flex;
  align-items: baseline;
  gap: 12px;
  margin-bottom: 20px;
`;

const CurrentPrice = styled.span`
  font-size: 28px;
  font-weight: 800;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const OriginalPrice = styled.span`
  font-size: 18px;
  color: ${({ theme }) => theme.colors.text.secondary};
  text-decoration: line-through;
`;

const SaveBadge = styled.span`
  background: #dcfce7;
  color: #15803d;
  font-size: 13px;
  font-weight: 600;
  padding: 4px 10px;
  border-radius: 20px;
`;

const OfferSelector = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  margin-bottom: 16px;
`;

const OfferSelectorButton = styled.button<{ $selected: boolean }>`
  border: 1px solid ${({ $selected }) => ($selected ? '#111827' : '#d1d5db')};
  background: ${({ $selected }) => ($selected ? '#111827' : '#ffffff')};
  color: ${({ $selected }) => ($selected ? '#ffffff' : '#111827')};
  border-radius: 999px;
  padding: 8px 12px;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
`;

const CTAButton = styled.button`
  width: 100%;
  padding: 14px;
  font-size: 16px;
  font-weight: 700;
  color: white;
  background: ${({ theme }) => theme.colors.text.primary};
  border: none;
  border-radius: 8px;
  cursor: pointer;
  transition: opacity 0.15s;

  &:hover { opacity: 0.9; }
`;

const DeclineLink = styled.button`
  display: block;
  margin: 12px auto 0;
  background: none;
  border: none;
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: 13px;
  cursor: pointer;
  text-decoration: underline;

  &:hover { color: ${({ theme }) => theme.colors.text.primary}; }
`;

interface FunnelModalProps {
  offer: FunnelOffer;
  offers?: FunnelOffer[];
  selectedOfferId?: string;
  onSelectOffer?: (offerId: string) => void;
  pageContent?: StepPageContent | StepPageBlock[] | null;
  errorMessage?: string | null;
  onAccept: () => void;
  onDecline: () => void;
  onDismiss: () => void;
  accepting?: boolean;
  displaySettings?: DisplaySettings | null;
  suppressOfferActions?: boolean;
  /** When false, backdrop clicks do not call onDismiss (default true). */
  closeOnOverlayClick?: boolean;
}

const MODAL_SIZE_MAP: Record<string, string> = {
  small: '360px',
  medium: '480px',
  large: '640px',
};

function safePrice(value: number | null | undefined): string {
  return Number.isFinite(value) ? `$${(value as number).toFixed(2)}` : '$0.00';
}

export function FunnelModal({ offer, offers, selectedOfferId, onSelectOffer, pageContent, errorMessage, onAccept, onDecline, onDismiss, accepting, displaySettings, suppressOfferActions, closeOnOverlayClick = true }: FunnelModalProps) {
  const displayOffer = withResolvedFunnelOfferPrice(offer);
  const offerImage = useOfferImage(offer.imageUrl, offer.productId);
  const [isClosing, setIsClosing] = useState(false);
  const closeTimerRef = useRef<ReturnType<typeof setTimeout>>();
  const blocks = normalizeBlocks(pageContent);
  const hasBlocks = blocks.length > 0;
  const hasCtaBlock = hasBlocks && blocksHaveCta(blocks);
  const multiOffers = Array.isArray(offers) && offers.length > 1 ? offers : null;

  const startClose = useCallback(
    (action: () => void) => {
      setIsClosing(true);
      closeTimerRef.current = setTimeout(() => {
        setIsClosing(false);
        action();
      }, CLOSE_ANIMATION_MS);
    },
    []
  );

  useEffect(() => {
    return () => clearTimeout(closeTimerRef.current);
  }, []);

  const callbacks: BlockCallbacks = {
    onAccept: suppressOfferActions ? undefined : () => startClose(onAccept),
    onDecline: () => startClose(onDecline),
    accepting,
    suppressOfferActions,
  };

  const isFullscreen = displaySettings?.size === 'large' && displaySettings?.fullscreenLayout;
  const modalMaxWidth = isFullscreen
    ? '100vw'
    : displaySettings?.size === 'custom' && displaySettings.customWidth
      ? `${displaySettings.customWidth}px`
      : MODAL_SIZE_MAP[displaySettings?.size ?? ''] ?? undefined;
  const modalStyle: React.CSSProperties = {
    ...(modalMaxWidth ? { maxWidth: modalMaxWidth } : {}),
    ...(isFullscreen ? { width: '100vw', height: '100vh', maxHeight: '100vh', borderRadius: 0 } : {}),
    ...(isFullscreen && displaySettings?.fullscreenLayout === 'two-column' ? { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px' } : {}),
  };

  const handleOverlayClick = closeOnOverlayClick
    ? () => startClose(onDismiss)
    : undefined;

  return (
    <Overlay onClick={handleOverlayClick} $closing={isClosing}>
      <ModalCard onClick={(e) => e.stopPropagation()} $closing={isClosing} style={modalStyle}>
        <CloseButton onClick={() => startClose(onDismiss)} aria-label="Close">&times;</CloseButton>

        {hasBlocks ? (
          <>
            {multiOffers && onSelectOffer && (
              <OfferSelector aria-label="Offer selector">
                {multiOffers.map((item, index) => {
                  const isSelected = (selectedOfferId ?? offer.id) === item.id || (!selectedOfferId && index === 0);
                  return (
                    <OfferSelectorButton
                      key={item.id}
                      type="button"
                      $selected={isSelected}
                      aria-pressed={isSelected}
                      onClick={() => onSelectOffer(item.id)}
                    >
                      {item.name || item.headline || item.id}
                    </OfferSelectorButton>
                  );
                })}
              </OfferSelector>
            )}
            <BlockContent>{renderBlocks(blocks, displayOffer, callbacks)}</BlockContent>
            {errorMessage && <Description style={{ color: '#b42318', marginTop: 12 }}>{errorMessage}</Description>}
            {!hasCtaBlock && (
              <>
                {!suppressOfferActions && (
                  <CTAButton onClick={() => startClose(onAccept)} disabled={accepting} style={{ marginTop: 16 }}>
                    {accepting ? 'Adding...' : offer.ctaText}
                  </CTAButton>
                )}
                <DeclineLink onClick={() => startClose(onDecline)}>{offer.declineText}</DeclineLink>
              </>
            )}
          </>
        ) : (
          <>
            {multiOffers && onSelectOffer && (
              <OfferSelector aria-label="Offer selector">
                {multiOffers.map((item, index) => {
                  const isSelected = (selectedOfferId ?? offer.id) === item.id || (!selectedOfferId && index === 0);
                  return (
                    <OfferSelectorButton
                      key={item.id}
                      type="button"
                      $selected={isSelected}
                      aria-pressed={isSelected}
                      onClick={() => onSelectOffer(item.id)}
                    >
                      {item.name || item.headline || item.id}
                    </OfferSelectorButton>
                  );
                })}
              </OfferSelector>
            )}
            {offerImage && <OfferImage src={offerImage} alt={offer.headline} />}
            <Headline>{offer.headline}</Headline>
            {offer.description && <Description>{offer.description}</Description>}
            {errorMessage && <Description style={{ color: '#b42318' }}>{errorMessage}</Description>}
            <PriceRow>
              <CurrentPrice>{safePrice(displayOffer.pricingValue)}</CurrentPrice>
              <OriginalPrice>{safePrice(offer.originalPrice)}</OriginalPrice>
              {((offer.originalPrice ?? 0) - (displayOffer.pricingValue ?? 0)) > 0 && (
                <SaveBadge>Save {safePrice((offer.originalPrice ?? 0) - (displayOffer.pricingValue ?? 0))}</SaveBadge>
              )}
            </PriceRow>
            {!suppressOfferActions && (
              <CTAButton onClick={() => startClose(onAccept)} disabled={accepting}>
                {accepting ? 'Adding...' : offer.ctaText}
              </CTAButton>
            )}
            <DeclineLink onClick={() => startClose(onDecline)}>{offer.declineText}</DeclineLink>
          </>
        )}
      </ModalCard>
    </Overlay>
  );
}
